/** Pre-save review dialog for the unified registration form. */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, X, Pencil, CheckCircle2 } from "lucide-react";

function PreviewBlock({ title, children }) {
  return (
    <div className="rounded-2xl border p-3">
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">{title}</div>
      <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1">{children}</dl>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <>
      <dt className="text-xs text-muted-foreground">{k}</dt>
      <dd className="text-sm font-medium break-words">{v}</dd>
    </>
  );
}

export function RegistrationPreview({ open, onOpenChange, form, busy, onCancel, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="new-reg-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Eye className="w-5 h-5 text-primary" /> Preview &amp; Save
          </DialogTitle>
          <DialogDescription>Review the entered details before saving.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <PreviewBlock title="Payment">
            <Row k="Amount" v={form.payment.amount ? `₹ ${Number(form.payment.amount).toLocaleString("en-IN")}` : "—"} />
            <Row k="Mode" v={form.payment.payment_type || "—"} />
            <Row k="Purpose" v={form.payment.purpose || "—"} />
          </PreviewBlock>

          <PreviewBlock title="Seller">
            <Row k="Name" v={form.seller.name || "—"} />
            <Row k="Mobile" v={form.seller.mobile || "—"} />
            <Row k="Aadhaar" v={form.seller.govt_id || "—"} />
            <Row k="Address" v={form.seller.address || "—"} />
          </PreviewBlock>

          {form.purchaser_available ? (
            <PreviewBlock title="Purchaser">
              <Row k="Name" v={form.purchaser.name || "—"} />
              <Row k="Mobile" v={form.purchaser.mobile || "—"} />
              <Row k="Aadhaar" v={form.purchaser.govt_id || "—"} />
              <Row k="Address" v={form.purchaser.address || "—"} />
            </PreviewBlock>
          ) : (
            <PreviewBlock title="Purchaser">
              <div className="text-xs text-muted-foreground">Not captured yet.</div>
            </PreviewBlock>
          )}

          {form.animals.map((a, idx) => (
            <PreviewBlock key={a._uid || idx} title={form.animals.length > 1 ? `Animal ${idx + 1}` : "Animal"}>
              <Row k="Type" v={a.animal_type || "—"} />
              <Row k="Breed" v={(a.breed === "Other" ? a.breed_custom : a.breed) || "—"} />
              <Row k="Gender" v={a.gender || "—"} />
              <Row k="Colour" v={(a.colour === "Other" ? a.colour_custom : a.colour) || "—"} />
              <Row k="Ear tag" v={a.ear_tag || "—"} />
              <Row k="Age" v={(a.age_years || a.age_months) ? `${a.age_years || 0}y ${a.age_months || 0}m` : "—"} />
              <Row k="Weight (kg)" v={a.weight_kg || "—"} />
              <Row k="Sale price" v={a.sale_price ? `₹ ${Number(String(a.sale_price).replace(/\D/g, "")).toLocaleString("en-IN")}` : "—"} />
              <Row k="ID mark" v={(a.identification_mark === "Other" ? a.identification_custom : a.identification_mark) || "—"} />
            </PreviewBlock>
          ))}

          <PreviewBlock title="Vehicle">
            <Row k="Number" v={form.vehicle.vehicle_number || "—"} />
            <Row k="Driver name" v={form.vehicle.driver_name || "—"} />
            <Row k="Driver mobile" v={form.vehicle.driver_mobile || "—"} />
          </PreviewBlock>

          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-3 text-xs text-primary-foreground/80 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary shrink-0" />
            <span className="text-muted-foreground">On save, Registration Slip, QR Gate Pass and Receipt are generated automatically.</span>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="rounded-full" onClick={onCancel} data-testid="new-reg-preview-cancel">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="new-reg-preview-edit">
            <Pencil className="w-4 h-4 mr-1" /> Edit Details
          </Button>
          <Button data-testid="new-reg-preview-save" className="am-btn-primary" onClick={onSubmit} disabled={busy}>
            <CheckCircle2 className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Save Registration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
